/*
  Vemica - homepage Featured Collection card enhancements. 2026-08-12.

  WHAT THIS DOES
  Adds, at runtime, the controls the reference design needs that do not exist
  anywhere in the theme's card markup:

      heart  - wishlist toggle
      eye    - view the product
      - 1 +  quantity, and a compact cart button

  WHY AT RUNTIME AND NOT IN THE MARKUP
  snippets/card-product.liquid is 35 KB and shared by collection pages, search,
  related products and the quick-add modals. Editing it would put these controls
  everywhere and risk all of those surfaces. This script only walks cards inside
  .vemica-home-cards, which is on sections/featured-collection.liquid alone, so
  the blast radius is exactly the homepage Featured Collection carousels.

  RUNS ONCE, NOT SIX TIMES  <-- do not remove the guard below
  The homepage stacks SIX featured-collection sections, and this file is loaded
  from that section, so the browser executes the script tag six times. Without a
  guard that meant six document click listeners: one tap on "+" drove the
  quantity from 1 to 7, and the cart button added 21 units instead of 2. The
  wishlist heart toggled six times per click and so appeared to do nothing.
  Measured on the draft before the fix.

  Online Store 2.0 gives a section no way to know a sibling already loaded an
  asset (the same limitation documented at the top of featured-collection.liquid
  for stylesheets), so the guard lives here.

  THE CARD HAS DUPLICATE CONTAINERS - PICK THE VISIBLE ONE
  Dawn's card renders TWO .card__information blocks and TWO .card__content
  blocks. Measured on this theme:

      .card__information[0]  display:none   children: card__heading
      .card__information[1]  display:block  children: card__heading, card-information (holds the price)

  querySelector returns [0], so appending the quantity row there put it inside a
  hidden element - the DOM assertions passed ("31 bars present") while nothing
  rendered. Before that, targeting .card__content landed the row on top of the
  product image, because the first .card__content is the image overlay. Both bugs
  were caught by screenshot, not by counting elements. Always resolve the VISIBLE
  container, and re-check on later scans if layout is not ready yet.

  SINGLE VARIANT vs MULTIPLE VARIANTS
      one variant   -> <product-form> with a hidden input[name="id"]
      many variants -> a modal opener button ("Choose options")

  A bare cart icon cannot ask which size, so single-variant products POST
  /cart/add.js with the chosen quantity and show the - 1 + control, while
  multi-variant products hide the stepper and the cart icon CLICKS Dawn's own
  quick-add modal so the customer picks the size there. That is why the original
  button is only visually hidden by the CSS rather than removed.

  WISHLIST
  Reuses the theme's existing assets/collection-wishlist.js contract
  (.js-wishlist-btn + data-wishlist-id, localStorage 'vemica_wishlist'). If that
  script is on the page it owns the clicks; otherwise the fallback at the bottom
  handles them with the same storage key.

  NOT TOUCHED: FlexyPe, Buy Now, the bundle builder, discounts, checkout, the
  carousel, product pages, or any other homepage section.
*/
(function () {
  'use strict';

  if (window.__vemicaHomeCardsLoaded) return;
  window.__vemicaHomeCardsLoaded = true;

  var ROOT_SELECTOR = '.vemica-home-cards';
  var WISHLIST_KEY = 'vemica_wishlist';

  var HEART =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M12 20.5C7 17.4 4 14.3 4 10.8 4 8.4 5.9 6.5 8.3 6.5c1.4 0 2.8.7 3.7 1.9.9-1.2 2.3-1.9 3.7-1.9 2.4 0 4.3 1.9 4.3 4.3 0 3.5-3 6.6-8 9.7z"/></svg>';

  var EYE =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M2.5 12S6 6.5 12 6.5 21.5 12 21.5 12 18 17.5 12 17.5 2.5 12 2.5 12z"/>' +
    '<circle cx="12" cy="12" r="2.7"/></svg>';

  var CART =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M3 4h2.2l2.2 10.4h9.6L19.5 7H7"/>' +
    '<circle cx="9.5" cy="19" r="1.4"/><circle cx="16.5" cy="19" r="1.4"/></svg>';

  function root() {
    return window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
  }

  function handleFromUrl(url) {
    var m = String(url || '').match(/\/products\/([^?#/]+)/);
    return m ? m[1] : '';
  }

  function wishlistHandled() {
    return typeof window.syncWishlistButtons === 'function';
  }

  function readWishlist() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'); } catch (e) { return []; }
  }

  function paintHeart(btn) {
    var on = readWishlist().indexOf(btn.getAttribute('data-wishlist-id')) > -1;
    btn.classList.toggle('is-active', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  // The VISIBLE .card__information - see the header for why this matters.
  function visibleInfo(card) {
    var infos = card.querySelectorAll('.card__information');
    for (var i = 0; i < infos.length; i++) {
      if (infos[i].offsetParent !== null) return infos[i];
    }
    return null;
  }

  function enhance(card) {
    if (card.getAttribute('data-vhc') === '1') return;

    var link = card.querySelector('.card__heading a, a.card__link, .card__information a');
    var href = link ? link.getAttribute('href') : '';
    var handle = handleFromUrl(href);

    /* ---------------------------------------------- heart + eye on image */
    var imageBox = card.querySelector('.card__inner');
    if (imageBox && !imageBox.querySelector('.vhc-icons')) {
      var icons = document.createElement('div');
      icons.className = 'vhc-icons';

      var heart = document.createElement('button');
      heart.type = 'button';
      heart.className = 'vhc-icon js-wishlist-btn';
      heart.setAttribute('data-wishlist-id', handle || href || 'product');
      heart.setAttribute('aria-label', 'Save to wishlist');
      heart.innerHTML = HEART;
      icons.appendChild(heart);

      var eye = document.createElement('a');
      eye.className = 'vhc-icon';
      eye.setAttribute('href', href || '#');
      eye.setAttribute('aria-label', 'View product');
      eye.innerHTML = EYE;
      icons.appendChild(eye);

      imageBox.appendChild(icons);
      paintHeart(heart);
    }

    /* ------------------------------------------- quantity + cart control */
    var content = visibleInfo(card);
    // Not laid out yet (lazy carousel). Leave unmarked so a later scan retries.
    if (!content) return;
    if (content.querySelector('.vhc-bar')) { card.setAttribute('data-vhc', '1'); return; }

    var form = card.querySelector('product-form form, form[action*="/cart/add"]');
    var variantInput = form ? form.querySelector('input[name="id"]') : null;
    var modalOpener = card.querySelector('.quick-add__submit, modal-opener button, [aria-haspopup="dialog"]');
    var singleVariant = !!(variantInput && variantInput.value);
    var soldOut = !!(card.querySelector('.badge--sold-out') ||
      (modalOpener && modalOpener.hasAttribute('disabled')));

    var bar = document.createElement('div');
    bar.className = 'vhc-bar' + (soldOut ? ' is-unavailable' : '');

    if (singleVariant) {
      var qty = document.createElement('div');
      qty.className = 'vhc-qty';
      qty.innerHTML =
        '<button type="button" class="vhc-qty__btn" data-vhc-step="-1" aria-label="Decrease quantity" disabled>−</button>' +
        '<span class="vhc-qty__val" data-vhc-qty>1</span>' +
        '<button type="button" class="vhc-qty__btn" data-vhc-step="1" aria-label="Increase quantity">+</button>';
      bar.appendChild(qty);
    } else {
      bar.appendChild(document.createElement('span'));
    }

    var cart = document.createElement('button');
    cart.type = 'button';
    cart.className = 'vhc-cart';
    cart.setAttribute('data-vhc-add', '1');
    cart.setAttribute('aria-label', singleVariant ? 'Add to cart' : 'Choose options');
    cart.innerHTML = CART;
    if (soldOut) cart.setAttribute('disabled', '');
    bar.appendChild(cart);

    content.appendChild(bar);
    card.setAttribute('data-vhc', '1');
  }

  function scan(scope) {
    var roots = (scope || document).querySelectorAll(ROOT_SELECTOR);
    Array.prototype.forEach.call(roots, function (r) {
      Array.prototype.forEach.call(r.querySelectorAll('.card-wrapper'), enhance);
    });
  }

  /* ------------------------------------------------------------ actions */

  function stepQty(btn) {
    var box = btn.closest('.vhc-qty');
    var valEl = box.querySelector('[data-vhc-qty]');
    var step = parseInt(btn.getAttribute('data-vhc-step'), 10);
    var next = Math.max(1, (parseInt(valEl.textContent, 10) || 1) + step);
    valEl.textContent = next;
    var minus = box.querySelector('[data-vhc-step="-1"]');
    if (minus) minus.disabled = next <= 1;
  }

  function addToCart(btn) {
    var card = btn.closest('.card-wrapper');
    if (!card) return;
    if (btn.hasAttribute('disabled')) return;

    var form = card.querySelector('product-form form, form[action*="/cart/add"]');
    var variantInput = form ? form.querySelector('input[name="id"]') : null;

    if (!variantInput || !variantInput.value) {
      var opener = card.querySelector('.quick-add__submit, modal-opener button, [aria-haspopup="dialog"]');
      if (opener) opener.click();
      return;
    }

    var valEl = card.querySelector('[data-vhc-qty]');
    var quantity = Math.max(1, parseInt(valEl ? valEl.textContent : '1', 10) || 1);

    btn.classList.add('is-loading');
    btn.setAttribute('disabled', '');

    fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantInput.value, 10), quantity: quantity }] }),
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); });
      })
      .then(function (cartData) {
        btn.classList.remove('is-loading');
        btn.removeAttribute('disabled');
        if (cartData && typeof cartData.item_count === 'number') {
          var bubbles = document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]');
          Array.prototype.forEach.call(bubbles, function (el) { el.textContent = cartData.item_count; });
        }
        if (valEl) valEl.textContent = '1';
        var minus = card.querySelector('[data-vhc-step="-1"]');
        if (minus) minus.disabled = true;
      })
      .catch(function () {
        btn.classList.remove('is-loading');
        btn.removeAttribute('disabled');
      });
  }

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || typeof t.closest !== 'function') return;
    if (!t.closest(ROOT_SELECTOR)) return;

    var step = t.closest('[data-vhc-step]');
    if (step) { event.preventDefault(); stepQty(step); return; }

    var add = t.closest('[data-vhc-add]');
    if (add) { event.preventDefault(); addToCart(add); return; }

    var heart = t.closest('.js-wishlist-btn');
    if (heart && !wishlistHandled()) {
      event.preventDefault();
      var hid = heart.getAttribute('data-wishlist-id');
      var list = readWishlist();
      var i = list.indexOf(hid);
      if (i > -1) list.splice(i, 1); else list.push(hid);
      try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); } catch (e) {}
      Array.prototype.forEach.call(document.querySelectorAll('.js-wishlist-btn'), paintHeart);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  // Cards arrive late from lazy loading, carousel paging and section re-renders.
  try {
    new MutationObserver(function () { scan(); })
      .observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) {}

  window.addEventListener('load', function () { scan(); });
})();
